import { openDB } from 'idb';

const DB_NAME = 'aitomotive-chat-cache';
const DB_VERSION = 3; // Bumped for sync metadata store
const STORE_CHATS = 'chats';
const STORE_THREADS = 'threads';
const STORE_AUTH = 'auth';
const STORE_SYNC = 'sync_meta';

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains(STORE_CHATS)) {
          const store = db.createObjectStore(STORE_CHATS, { keyPath: 'filtersKey' });
          store.createIndex('timestamp', 'timestamp');
        }
        if (!db.objectStoreNames.contains(STORE_THREADS)) {
          const store = db.createObjectStore(STORE_THREADS, { keyPath: 'chatId' });
          store.createIndex('timestamp', 'timestamp');
        }
        if (!db.objectStoreNames.contains(STORE_AUTH)) {
          db.createObjectStore(STORE_AUTH, { keyPath: 'key' });
        }
        // New in version 3: sync metadata store
        if (!db.objectStoreNames.contains(STORE_SYNC)) {
          db.createObjectStore(STORE_SYNC, { keyPath: 'key' });
        }
      }
    });
  }
  return dbPromise;
}

export async function getChatCache(filtersKey) {
  try {
    const db = await getDB();
    return await db.get(STORE_CHATS, filtersKey);
  } catch (e) {
    console.warn('[db] getChatCache failed', e);
    return null;
  }
}

export async function setChatCache(filtersKey, chats, pagination) {
  try {
    const db = await getDB();
    const payload = {
      filtersKey,
      data: Array.isArray(chats) ? chats : [],
      pagination: pagination || { current_page: 1, total_pages: 1 },
      timestamp: Date.now(),
    };
    await db.put(STORE_CHATS, payload);
  } catch (e) {
    console.warn('[db] setChatCache failed', e);
  }
}

export async function getThreadCache(chatId) {
  try {
    const db = await getDB();
    return await db.get(STORE_THREADS, String(chatId));
  } catch (e) {
    console.warn('[db] getThreadCache failed', e);
    return null;
  }
}

export async function setThreadCache(chatId, visibleMessages, pagination) {
  try {
    const db = await getDB();
    const payload = {
      chatId: String(chatId),
      visibleMessages: Array.isArray(visibleMessages) ? visibleMessages : [],
      pagination: pagination || null,
      timestamp: Date.now(),
    };
    await db.put(STORE_THREADS, payload);
  } catch (e) {
    console.warn('[db] setThreadCache failed', e);
  }
}

export async function deleteThreadCache(chatId) {
  try {
    const db = await getDB();
    await db.delete(STORE_THREADS, String(chatId));
  } catch (e) {
    console.warn('[db] deleteThreadCache failed', e);
  }
}

// Auth token storage for Service Worker access
export async function setAuthToken(token) {
  try {
    const db = await getDB();
    await db.put(STORE_AUTH, { key: 'token', value: token, timestamp: Date.now() });
    console.log('[db] ✅ Auth token saved to IndexedDB for SW access');
  } catch (e) {
    console.warn('[db] setAuthToken failed', e);
  }
}

export async function getAuthToken() {
  try {
    const db = await getDB();
    const result = await db.get(STORE_AUTH, 'token');
    return result?.value || null;
  } catch (e) {
    console.warn('[db] getAuthToken failed', e);
    return null;
  }
}

export async function clearAuthToken() {
  try {
    const db = await getDB();
    await db.delete(STORE_AUTH, 'token');
  } catch (e) {
    console.warn('[db] clearAuthToken failed', e);
  }
}

// ==================== Service Worker Communication ====================

/**
 * Trigger a background sync via Service Worker
 * @param {Object} options - Sync options
 * @param {string} options.backendUrl - Backend URL for API calls
 * @param {string} options.reason - Reason for sync (for logging)
 * @param {boolean} options.force - Force sync even if rate limited
 * @returns {Promise<Object>} Sync result
 */
export async function triggerServiceWorkerSync(options = {}) {
  if (!('serviceWorker' in navigator)) {
    console.warn('[db] Service Worker not supported');
    return { success: false, reason: 'not_supported' };
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Try Background Sync API first (most reliable)
    if ('sync' in registration) {
      await registration.sync.register('chat-background-sync');
      console.log('[db] Background sync registered');
    }
    
    // Also send message for immediate sync
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data || { success: true });
      };
      
      // Timeout after 10s
      const timeout = setTimeout(() => {
        resolve({ success: false, reason: 'timeout' });
      }, 10000);
      
      navigator.serviceWorker.controller?.postMessage(
        { type: 'TRIGGER_SYNC', data: options },
        [messageChannel.port2]
      );
      
      // Clear timeout on response
      messageChannel.port1.onmessage = (event) => {
        clearTimeout(timeout);
        resolve(event.data || { success: true });
      };
    });
  } catch (e) {
    console.warn('[db] triggerServiceWorkerSync failed:', e);
    return { success: false, error: e.message };
  }
}

/**
 * Set backend URL in Service Worker for future syncs
 */
export async function setServiceWorkerBackendUrl(url) {
  if (!('serviceWorker' in navigator)) {
    console.warn('[db] Service Worker not supported, cannot set backend URL');
    return;
  }
  
  if (!navigator.serviceWorker.controller) {
    console.warn('[db] No SW controller yet, will save backend URL directly to IndexedDB');
    // Save directly to IndexedDB as fallback
    try {
      const db = await getDB();
      await db.put(STORE_SYNC, { key: 'backend_url', value: url });
      console.log('[db] ✅ Backend URL saved to IndexedDB:', url);
    } catch (e) {
      console.warn('[db] Failed to save backend URL to IndexedDB:', e);
    }
    return;
  }
  
  console.log('[db] 📤 Sending backend URL to SW:', url);
  navigator.serviceWorker.controller.postMessage({
    type: 'SET_BACKEND_URL',
    data: { url }
  });
}

/**
 * Get sync status from Service Worker
 */
export async function getServiceWorkerSyncStatus() {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    return null;
  }
  
  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    const timeout = setTimeout(() => resolve(null), 2000);
    
    messageChannel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      resolve(event.data);
    };
    
    navigator.serviceWorker.controller.postMessage(
      { type: 'GET_SYNC_STATUS' },
      [messageChannel.port2]
    );
  });
}

/**
 * Register for periodic background sync (if supported)
 */
export async function registerPeriodicSync(intervalMinutes = 5) {
  if (!('serviceWorker' in navigator)) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    if ('periodicSync' in registration) {
      const status = await navigator.permissions.query({
        name: 'periodic-background-sync',
      });
      
      if (status.state === 'granted') {
        await registration.periodicSync.register('chat-periodic-sync', {
          minInterval: intervalMinutes * 60 * 1000,
        });
        console.log('[db] Periodic sync registered');
        return true;
      }
    }
  } catch (e) {
    console.warn('[db] Periodic sync registration failed:', e);
  }
  
  return false;
}

// ==================== Sync Metadata Helpers ====================

export async function getSyncTimestamp() {
  try {
    const db = await getDB();
    const result = await db.get(STORE_SYNC, 'last_sync');
    return result?.value || null;
  } catch (e) {
    return null;
  }
}

export async function setSyncTimestamp(timestamp) {
  try {
    const db = await getDB();
    await db.put(STORE_SYNC, { key: 'last_sync', value: timestamp, updated: Date.now() });
  } catch (e) {
    console.warn('[db] setSyncTimestamp failed', e);
  }
}

// Optional: simple LRU/TTL cleanup helpers can be added later
